// src/hooks/useSnapshots.ts
import { Snapshot, SnapshotFilters, snapshotsService } from '@/services/snapshot.service';
import { useCallback, useEffect, useState } from 'react';

interface UseSnapshotHistorialResult {
  data: Snapshot[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseSnapshotResult {
  data: Snapshot | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseSnapshotCountResult {
  count: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook para obtener el historial de snapshots de una empresa
 */
export function useSnapshotHistorial(nit: string, filters?: SnapshotFilters, enabled: boolean = true): UseSnapshotHistorialResult {
  const [data, setData] = useState<Snapshot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistorial = useCallback(async () => {
    if (!enabled || !nit) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await snapshotsService.getHistorial(nit, filters);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al cargar historial de snapshots');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar historial de snapshots');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit, filters?.fechaInicio, filters?.fechaFin]);

  useEffect(() => {
    fetchHistorial();
  }, [fetchHistorial]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchHistorial,
  };
}

/**
 * Hook para obtener el último snapshot de una empresa
 */
export function useUltimoSnapshot(nit: string, enabled: boolean = true): UseSnapshotResult {
  const [data, setData] = useState<Snapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUltimo = useCallback(async () => {
    if (!enabled || !nit) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await snapshotsService.getUltimo(nit);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al cargar último snapshot');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar último snapshot');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit]);

  useEffect(() => {
    fetchUltimo();
  }, [fetchUltimo]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchUltimo,
  };
}

/**
 * Hook para obtener un snapshot por fecha específica
 */
export function useSnapshotByFecha(nit: string, fecha: string | null, enabled: boolean = true): UseSnapshotResult {
  const [data, setData] = useState<Snapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchByFecha = useCallback(async () => {
    if (!enabled || !nit || !fecha) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await snapshotsService.getByFecha(nit, fecha);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al cargar snapshot por fecha');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar snapshot por fecha');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit, fecha]);

  useEffect(() => {
    fetchByFecha();
  }, [fetchByFecha]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchByFecha,
  };
}

/**
 * Hook para contar snapshots de una empresa
 */
export function useSnapshotCount(nit: string, filters?: SnapshotFilters, enabled: boolean = true): UseSnapshotCountResult {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async () => {
    if (!enabled || !nit) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await snapshotsService.count(nit, filters);
      if (response.success) {
        setCount(response.data.count);
      } else {
        setError('Error al contar snapshots');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al contar snapshots');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit, filters?.fechaInicio, filters?.fechaFin]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return {
    count,
    isLoading,
    error,
    refetch: fetchCount,
  };
}
